import './accounts.css';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { emailOk, formatIban, ibanOk, LEGAL_FORMS, normalizeIban, warnings, zipOk } from '../../auth/company';
import { fmtDate } from '../../lib/format';
import { eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import type { Ustva } from '../../types';
import { LinkDialog } from './LinkDialog';
import { MandatBeenden } from './MandatBeenden';
import { Papierkorb } from './Papierkorb';
import { PruefungExport } from './PruefungExport';
import { addressLine, AREAS, consentText, fitsArea, staffLabel, USTVA_NOTE, USTVA_OPTS, ustvaLabel } from './labels';
import type { ClientDetail, LinkResult, StaffOption } from './types';
import { Mandatsvertrag } from '../vertrag/Vertrag';

// Tab „Stammdaten“: Stammdaten (ansehen, bearbeiten), App-Zugang (Passwort-Link), Betreuung (UStVA, Zuständigkeit,
// Monatsbetrag der Buchhaltung, KI, Löschsperre), Papierkorb, Export für die Betriebsprüfung und Mandat beenden (Admin).
export function StammdatenTab({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const [editing, setEditing] = useState(false);
  // Neuer Schlüssel = Formular neu mit den aktuellen Angaben füllen
  const [formKey, setFormKey] = useState(0);
  return (
    <div className="grid2 acc">
      <section className="card" aria-labelledby="st-h">
        <div className="ch">
          <h3 id="st-h">Stammdaten</h3>
          {!editing && (
            <button type="button" className="btn sm" onClick={() => setEditing(true)}>
              Bearbeiten
            </button>
          )}
        </div>
        {editing ? (
          <EditForm
            key={formKey}
            detail={detail}
            reload={reload}
            done={() => setEditing(false)}
            restart={async () => {
              await reload();
              setFormKey((k) => k + 1);
            }}
          />
        ) : (
          <View detail={detail} />
        )}
      </section>
      <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
        <AppAccess detail={detail} />
        <Care detail={detail} reload={reload} />
        {detail.access.isAdmin && detail.client.status === 'active' && (
          <Mandatsvertrag clientId={detail.client.id} clientName={detail.client.name} />
        )}
        {detail.access.isAdmin && <Papierkorb clientId={detail.client.id} />}
        {detail.access.isAdmin && <PruefungExport clientId={detail.client.id} />}
        {detail.access.isAdmin && detail.client.status === 'active' && <MandatBeenden detail={detail} reload={reload} />}
      </div>
    </div>
  );
}

function Pair({ k, v, warn }: { k: string; v: ReactNode; warn?: string | null }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>
        {v === null || v === undefined || v === '' ? '–' : v}
        {warn && (
          <>
            {' '}
            <span className="pill p-warn">⚠ {warn}</span>
          </>
        )}
      </dd>
    </>
  );
}

function View({ detail }: { detail: ClientDetail }) {
  const c = detail.client;
  // Warnhinweise wie beim Prüfen der Registrierung (alle Mandanten haben sich selbst registriert)
  const w = warnings(c);
  return (
    <>
      <dl className="dl">
        <Pair k="Mandanten-Nr." v={c.number} />
        <Pair k="Firma" v={c.name} />
        <Pair k="Rechtsform" v={c.legalForm} warn={w.legalForm} />
        <Pair k="Adresse" v={addressLine(c)} warn={w.zip} />
        <Pair k="Land" v={c.country === 'AE' ? 'Dubai (VAE)' : 'Deutschland'} />
        <Pair k="Ansprechpartner" v={c.contactName} />
        <Pair k="E-Mail (Login)" v={c.loginEmail} />
        <Pair k="Telefon" v={c.contactPhone} />
        <Pair k="Steuernummer" v={c.taxNumber} />
        <Pair k={c.country === 'AE' ? 'TRN' : 'USt-ID'} v={c.vatId} />
      </dl>
      <div className="ch" style={{ marginTop: 16 }}>
        <h3>Bankverbindung</h3>
      </div>
      <dl className="dl">
        <Pair k="Kontoinhaber" v={c.accountHolder} />
        <Pair k="IBAN" v={c.iban ? formatIban(c.iban) : null} warn={w.iban} />
        <Pair k="BIC" v={c.bic} />
        <Pair k="Bank" v={c.bankName} />
      </dl>
    </>
  );
}

const FIELDS = [
  ['street', 'Straße'],
  ['zip', 'PLZ'],
  ['city', 'Ort'],
  ['contactName', 'Ansprechpartner'],
  ['loginEmail', 'E-Mail (Login)'],
  ['contactPhone', 'Telefon'],
  ['taxNumber', 'Steuernummer'],
  ['vatId', 'USt-ID'],
  ['accountHolder', 'Kontoinhaber'],
  ['iban', 'IBAN'],
  ['bic', 'BIC'],
  ['bankName', 'Bank'],
] as const;
type FieldKey = (typeof FIELDS)[number][0] | 'name' | 'legalForm' | 'number';

function EditForm({
  detail,
  reload,
  done,
  restart,
}: {
  detail: ClientDetail;
  reload(): Promise<void>;
  done(): void;
  restart(): Promise<void>;
}) {
  const c = detail.client;
  const admin = detail.access.isAdmin;
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Stand, auf dem dieses Formular beruht. Live-Aktualisierungen ändern ihn nicht, erst „Speichern“ prüft ihn.
  const [version] = useState(c.version);
  const [v, setV] = useState<Record<FieldKey, string>>(() => ({
    name: c.name,
    legalForm: c.legalForm,
    number: c.number ?? '',
    street: c.street,
    zip: c.zip,
    city: c.city,
    contactName: c.contactName,
    loginEmail: c.loginEmail ?? '',
    contactPhone: c.contactPhone ?? '',
    taxNumber: c.taxNumber ?? '',
    vatId: c.vatId ?? '',
    accountHolder: c.accountHolder,
    iban: c.iban ? formatIban(c.iban) : '',
    bic: c.bic ?? '',
    bankName: c.bankName ?? '',
  }));
  const set = (k: FieldKey, value: string) => setV((x) => ({ ...x, [k]: value }));
  const focus = (k: FieldKey) => document.getElementById(`s-${k}`)?.focus();
  const fail = (msg: string, k: FieldKey) => {
    toast(msg);
    focus(k);
  };
  // Alte Rechtsformen, die nicht in der Liste stehen, bleiben wählbar
  const forms: string[] = LEGAL_FORMS.includes(c.legalForm as (typeof LEGAL_FORMS)[number]) ? [...LEGAL_FORMS] : [c.legalForm, ...LEGAL_FORMS];

  async function save(e: FormEvent) {
    e.preventDefault();
    const nr = v.number.trim();
    if (admin && !/^\d{5}$/.test(nr)) return fail('Die Mandantennummer muss 5-stellig sein', 'number');
    // IBAN und Nummer nur prüfen, wenn sie sich geändert haben (Altdaten bleiben speicherbar)
    const iban = normalizeIban(v.iban);
    if (iban && iban !== normalizeIban(c.iban) && !ibanOk(iban)) return fail('Die IBAN scheint nicht zu stimmen. Bitte prüfen.', 'iban');
    if (v.zip.trim() && !zipOk(v.zip)) return fail('Die PLZ muss 5-stellig sein.', 'zip');
    if (!v.contactName.trim()) return fail('Bitte geben Sie den Ansprechpartner ein.', 'contactName');
    if (admin && !emailOk(v.loginEmail)) return fail('Bitte geben Sie eine gültige E-Mail-Adresse ein.', 'loginEmail');
    setBusy(true);
    try {
      await api.patch(`/api/kanzlei/clients/${c.id}`, { ...v, version, number: admin ? nr : undefined, iban });
      toast('Stammdaten gespeichert');
      done();
      await reload();
    } catch (err) {
      toast(errorText(err));
      const code = err instanceof ApiError ? err.code : undefined;
      // Inzwischen hat jemand anderes gespeichert: Formular mit den neuen Angaben füllen, dann noch einmal ändern
      if (code === 'stale') {
        await restart();
        return;
      }
      if (code === 'number_taken' || code === 'bad_number') focus('number');
      else if (code === 'bad_iban') focus('iban');
      else if (code === 'email_taken') focus('loginEmail');
    } finally {
      setBusy(false);
    }
  }

  const input = (k: FieldKey, label: ReactNode, extra: Record<string, unknown> = {}) => (
    <label htmlFor={`s-${k}`} key={k}>
      {label}
      <input id={`s-${k}`} value={v[k]} onChange={(e) => set(k, e.target.value)} maxLength={160} {...extra} />
    </label>
  );

  return (
    <form className="form" onSubmit={save} noValidate>
      {input('name', 'Firmenname', { required: true, autoComplete: 'organization' })}
      <label htmlFor="s-legalForm">
        Rechtsform
        <select id="s-legalForm" value={v.legalForm} onChange={(e) => set('legalForm', e.target.value)}>
          {forms.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </label>
      {admin
        ? input('number', 'Mandantennummer', { required: true, inputMode: 'numeric', pattern: '\\d{5}', maxLength: 5, title: '5-stellige Zahl' })
        : input(
            'number',
            <>
              Mandantennummer <span className="note">(vergibt der Admin)</span>
            </>,
            { readOnly: true },
          )}
      {FIELDS.map(([k, label]) => {
        if (k === 'zip') return input(k, label, { inputMode: 'numeric', pattern: '\\d{5}', maxLength: 5, title: '5-stellige PLZ' });
        // Der Ansprechpartner ist der Name des Logins und gilt für alle seine Firmen. Gehört der Login auch zu Firmen,
        // die der Mitarbeiter nicht betreut, ändert ihn der Admin.
        if (k === 'contactName') {
          return detail.access.contactName
            ? input(k, label, { required: true })
            : input(
                k,
                <>
                  {label} <span className="note">(ändert der Admin, gilt auch für Firmen, die Sie nicht betreuen)</span>
                </>,
                { readOnly: true },
              );
        }
        // Die Login-E-Mail ändert nur der Admin (damit lässt sich der Zugang des Mandanten übernehmen)
        if (k === 'loginEmail') {
          return admin
            ? input(k, label, { type: 'email', required: true })
            : input(
                k,
                <>
                  {label} <span className="note">(ändert der Admin)</span>
                </>,
                { type: 'email', readOnly: true },
              );
        }
        if (k === 'contactPhone') return input(k, label, { type: 'tel' });
        if (k === 'vatId') return input(k, c.country === 'AE' ? 'TRN' : 'USt-ID');
        if (k === 'iban') return input(k, label, { autoCapitalize: 'characters', spellCheck: false, maxLength: 42 });
        return input(k, label);
      })}
      <div className="btnrow">
        <button type="submit" className="btn sm dark" disabled={busy}>
          Speichern
        </button>
        <button type="button" className="btn sm" onClick={done}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// Karte „App-Zugang“ mit „Passwort zurücksetzen“. Mitarbeiter nur mit E-Mail-Versand (der Link geht an die
// Login-E-Mail); einen Link zum Weitergeben bekommt nur der Admin, denn er öffnet den Login mit allen Firmen.
function AppAccess({ detail }: { detail: ClientDetail }) {
  const c = detail.client;
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  async function reset() {
    setBusy(true);
    try {
      const res = await api.post<LinkResult>(`/api/kanzlei/clients/${c.id}/password-reset`);
      if (res.sent) toast(`Link zum Zurücksetzen des Passworts an ${res.email ?? c.loginEmail} geschickt`);
      else setLink(res.link ?? null);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="app-h">
      <div className="ch">
        <h3 id="app-h">App-Zugang</h3>
      </div>
      {c.status === 'ended' ? (
        <p>
          <span className="pill p-bad">gesperrt</span> Mandat beendet
        </p>
      ) : (
        <p>
          <span className="pill p-ok">freigeschaltet</span>
          {c.since && <> seit {fmtDate(c.since)}</>}
        </p>
      )}
      <p className="note">{consentText(c.consent)}</p>
      {c.status === 'ended' ? null : c.loginEmail ? (
        <>
          <p className="note">Login: {c.loginEmail}. Das Passwort hat der Mandant bei der Registrierung selbst festgelegt.</p>
          {detail.access.passwordReset ? (
            <div className="btnrow" style={{ marginTop: 10 }}>
              <button type="button" className="btn sm" onClick={() => void reset()} disabled={busy}>
                Passwort zurücksetzen
              </button>
            </div>
          ) : (
            <p className="note">Ohne E-Mail-Versand gibt der Admin den Link zum Zurücksetzen des Passworts weiter.</p>
          )}
        </>
      ) : (
        <p className="note">Für diesen Mandanten gibt es keinen Login.</p>
      )}
      <LinkDialog
        link={link}
        title="Link zum Zurücksetzen"
        text={
          <>
            Es ist kein E-Mail-Versand eingerichtet. Bitte schicken Sie diesen Link an {c.contactName} ({c.loginEmail}). Darüber
            wird ein neues Passwort festgelegt. Der Link gilt 24 Stunden.
          </>
        }
        onClose={() => setLink(null)}
      />
    </section>
  );
}

// Karte „Betreuung“: UStVA-Einstufung (FiBu), Zuständigkeit und Monatsbetrag (Admin) bzw. Hinweis für Mitarbeiter
function Care({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const admin = detail.access.isAdmin;
  const toast = useToast();
  const location = useLocation();
  const staff = useApi<{ staff: StaffOption[] }>(admin ? '/api/kanzlei/staff' : null, { live: ['team'] });
  const [ask, setAsk] = useState<Ustva | null>(null);
  const [busy, setBusy] = useState(false);
  const fibuRef = useRef<HTMLSelectElement>(null);
  const lohnRef = useRef<HTMLSelectElement>(null);
  const r = c.ustva;

  // „Jetzt zuweisen“ aus dem Banner: Fokus auf das erste offene Auswahlfeld
  const wantsAssign = (location.state as { assign?: boolean } | null)?.assign === true;
  const staffLoaded = !!staff.data;
  useEffect(() => {
    if (!wantsAssign || !staffLoaded) return;
    (detail.missing.fibu ? fibuRef : lohnRef).current?.focus();
    // nur einmal nach dem Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAssign, staffLoaded]);

  async function setUstva(next: Ustva) {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/ustva`, { ustva: next });
      setAsk(null);
      toast(`UStVA-Einstufung: ${ustvaLabel(next)}`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function assign(area: 'fibu' | 'lohn', userId: string) {
    try {
      const res = await api.post<{ name: string | null }>(`/api/kanzlei/clients/${c.id}/assign`, { area, userId: userId || null });
      toast(`${c.name} · ${area === 'fibu' ? 'FiBu' : 'Lohn'} → ${res.name ?? 'nicht zugewiesen'}`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    }
  }

  const askLabel = ask ? ustvaLabel(ask) : '';
  return (
    <section className="card" aria-labelledby="care-h">
      <div className="ch">
        <h3 id="care-h">Betreuung</h3>
      </div>
      <div className="form">
        {c.country === 'DE' ? (
          <div className="grp" role="group" aria-labelledby="ustva-l">
            <span id="ustva-l">UStVA-Einstufung</span>
            {detail.access.fibu ? (
              <span className="seg">
                {USTVA_OPTS.map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={r === k}
                    onClick={() => setAsk(k === r ? null : k)}
                    disabled={busy}
                  >
                    {label}
                  </button>
                ))}
              </span>
            ) : (
              <b>{ustvaLabel(r)}</b>
            )}
            <span className="note">
              {USTVA_NOTE[r]} Steuert die Frist im Dashboard des Mandanten.{detail.access.fibu ? '' : ' Ändern kann die FiBu.'}
            </span>
          </div>
        ) : (
          <p className="note">Dubai-Mandant: VAT quartalsweise über die FTA.</p>
        )}
        {ask && detail.access.fibu && (
          <div className="confirmup" role="group" aria-labelledby="ask-h">
            <b id="ask-h">Auf „{askLabel}“ umstellen?</b>
            <div className="btnrow">
              <button type="button" className="btn sm dark" onClick={() => void setUstva(ask)} disabled={busy}>
                Ja, umstellen
              </button>
              <button type="button" className="btn sm" onClick={() => setAsk(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        )}
        <BrancheField detail={detail} reload={reload} />
        {admin ? (
          AREAS.map((a) => {
            const current = detail[a.key]?.id ?? '';
            // nur passend zum Bereich (Lohn: Lohn oder beides, FiBu: FiBu oder beides)
            const options = (staff.data?.staff ?? []).filter((s) => fitsArea(s, a.key));
            return (
              <label htmlFor={`sel-${a.key}`} key={a.key}>
                Zuständig für {a.label}
                <select
                  id={`sel-${a.key}`}
                  ref={a.key === 'fibu' ? fibuRef : lohnRef}
                  value={current}
                  onChange={(e) => void assign(a.key, e.target.value)}
                  disabled={!staff.data}
                >
                  <option value="">nicht zugewiesen</option>
                  {/* Zuständige, die gerade nicht in der Liste sind, trotzdem anzeigen */}
                  {current && !options.some((s) => s.id === current) && <option value={current}>{detail[a.key]?.name}</option>}
                  {options.map((s) => (
                    <option key={s.id} value={s.id}>
                      {staffLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
            );
          })
        ) : (
          <div className="lock">
            Zuständig: FiBu: {detail.fibu?.name ?? 'offen'} · Lohn: {detail.lohn?.name ?? 'offen'}. Nur der Admin kann das ändern.
          </div>
        )}
        {admin && <Fee detail={detail} reload={reload} />}
        {admin && <KiSwitch detail={detail} reload={reload} />}
        {admin && <SperreSwitch detail={detail} reload={reload} />}
      </div>
    </section>
  );
}

// Branche wie in server/src/modules/lohn/branche.ts (Gesetzes-Prüfung A3, B7, B9)
const BRANCHEN: [string, string][] = [
  ['bau', 'Bau'],
  ['gebaeudereinigung', 'Gebäudereinigung'],
  ['sicherheit', 'Wach- und Sicherheitsdienst'],
  ['transport', 'Spedition, Transport, Logistik, Lieferdienst'],
  ['personenbefoerderung', 'Personenbeförderung (Taxi, Bus)'],
  ['gastronomie', 'Gastronomie und Hotel'],
  ['friseur', 'Friseur und Kosmetik'],
  ['messebau', 'Messe- und Ausstellungsbau'],
  ['fleisch', 'Fleischwirtschaft'],
  ['schausteller', 'Schausteller'],
  ['andere', 'Andere Branche'],
];

// Branche (Admin oder Lohn): steuert im Arbeitsvertrag des Mandanten Branchenmindestlohn, Tarifvertrag und den Satz
// zur Ausweispflicht
function BrancheField({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const label = BRANCHEN.find(([k]) => k === c.branche)?.[1];

  async function set(branche: string) {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/branche`, { branche: branche || null });
      toast(`Branche: ${BRANCHEN.find(([k]) => k === branche)?.[1] ?? 'nicht eingestellt'}`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const note = 'Steuert im Arbeitsvertrag Branchenmindestlohn, Tarifvertrag und Ausweispflicht.';
  if (!detail.access.lohn) {
    return (
      <div className="grp">
        <span>Branche</span>
        <b>{label ?? 'nicht eingestellt'}</b>
        <span className="note">{note} Ändern kann die Lohnbuchhaltung.</span>
      </div>
    );
  }
  return (
    <label htmlFor="sel-branche">
      Branche
      <select id="sel-branche" value={c.branche ?? ''} onChange={(e) => void set(e.target.value)} disabled={busy}>
        <option value="">nicht eingestellt</option>
        {BRANCHEN.map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
      <span className="note">{note}</span>
    </label>
  );
}

// KI je Mandant (Admin): Neue Mandanten starten mit KI aus. An erst, wenn der Mandant der KI in Textform zugestimmt hat
// (Mandatsvertrag), aus, wenn er das widerruft. Ohne KI werden Briefe, BWA und Mitarbeiterlisten von Hand eingetragen.
function KiSwitch({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function set(aus: boolean) {
    if (aus === c.kiAus) return;
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/ki`, { aus });
      toast(aus ? `KI für ${c.name} ausgeschaltet` : `KI für ${c.name} eingeschaltet`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grp" role="group" aria-labelledby="ki-l">
      <span id="ki-l">KI für diesen Mandanten</span>
      <span className="seg">
        <button type="button" aria-pressed={!c.kiAus} onClick={() => void set(false)} disabled={busy}>
          An
        </button>
        <button type="button" aria-pressed={c.kiAus} onClick={() => void set(true)} disabled={busy}>
          Aus
        </button>
      </span>
      <span className="note">
        {c.kiAus
          ? 'Briefe, BWA und Mitarbeiterlisten dieses Mandanten gehen nicht an die KI, alles wird von Hand eingetragen. ' +
            'An erst, wenn der Mandant der KI in Textform zugestimmt hat.'
          : 'Die KI liest Briefe, BWA und Mitarbeiterlisten und macht Vorschläge. Aus, wenn der Mandant das widerruft.'}
        {c.kiOn ? '' : ' Die KI ist noch nicht eingerichtet.'}
      </span>
    </div>
  );
}

// Löschsperre (Admin): Die App löscht Belege, Lohnunterlagen, Briefe usw. nach Ablauf der Aufbewahrungsfrist
// automatisch; während einer Betriebsprüfung läuft die Frist nicht ab, dann hier anschalten.
function SperreSwitch({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function set(an: boolean) {
    if (an === c.loeschsperre) return;
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/loeschsperre`, { an });
      toast(an ? `Löschsperre für ${c.name} an` : `Löschsperre für ${c.name} aus`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grp" role="group" aria-labelledby="sperre-l">
      <span id="sperre-l">Löschsperre</span>
      <span className="seg">
        <button type="button" aria-pressed={!c.loeschsperre} onClick={() => void set(false)} disabled={busy}>
          Aus
        </button>
        <button type="button" aria-pressed={c.loeschsperre} onClick={() => void set(true)} disabled={busy}>
          An
        </button>
      </span>
      <span className="note">
        {c.loeschsperre
          ? 'Die App löscht für diesen Mandanten nichts automatisch, auch wenn Aufbewahrungsfristen abgelaufen sind.'
          : 'Nach Ablauf der Aufbewahrungsfristen löscht die App automatisch. An z. B. während einer Betriebsprüfung.'}
      </span>
    </div>
  );
}

// Monatsbetrag der Buchhaltung (Admin, netto): Setzt die FiBu einen Monat auf „gebucht“, kommt unter „Abrechnung“ und
// in der Glocke der Vorschlag, die Rechnung über diesen Betrag zu schreiben. Leer: kein Vorschlag.
function Fee({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const toast = useToast();
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const saved = c.monthlyFee ? c.monthlyFee.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  const value = text ?? saved;

  async function save(e: FormEvent) {
    e.preventDefault();
    const v = parseEuro(value);
    if (v === undefined || (v !== null && (v < 0 || v >= 1e6))) {
      setError('Bitte den Monatsbetrag prüfen, z. B. 150,00.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/fee`, { fee: v ? v : null });
      await reload();
      setText(null);
      toast(v ? `Buchhaltung monatlich: ${eurC(v)} netto` : 'Kein Monatsbetrag: keine Vorschläge für gebuchte Monate');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grp" onSubmit={save} noValidate>
      <label htmlFor="fee-in">Buchhaltung monatlich (€ netto)</label>
      <div className="fee-row">
        <input
          id="fee-in"
          className="num"
          value={value}
          inputMode="decimal"
          placeholder="z. B. 150,00"
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
        />
        <button type="submit" className="btn sm dark" disabled={busy || value === saved}>
          Übernehmen
        </button>
      </div>
      {error && (
        <span className="note fee-err" role="alert">
          {error}
        </span>
      )}
      <span className="note">
        Setzt die FiBu einen Monat auf „gebucht“, bekommen Sie unter „Abrechnung“ den Vorschlag, die Rechnung über diesen Betrag
        zu schreiben. Leer: kein Vorschlag.
      </span>
    </form>
  );
}
